import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from './ThemeContext';
import './App.css';
import BannerTable from './BannerTable';
import CouponTable from './CouponTable';

function App() {
  const navigate = useNavigate();
  const { theme, toggleTheme, isDark } = useTheme();
  const [ads, setAds] = useState([]);
  const [filteredAds, setFilteredAds] = useState([]);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalPages, setTotalPages] = useState(0);

  // Get current user from localStorage
  const getCurrentUser = () => {
    try {
      const authData = localStorage.getItem('auth');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.user;
      }
    } catch (error) {
      console.error('Error getting current user:', error);
    }
    return null;
  };

  const handleLogout = () => {
    // Clear authentication data from localStorage
    localStorage.removeItem('auth');
    // Redirect to login (AuthWrapper will handle showing login page)
    window.location.reload();
  };

  // Modal state for image preview
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [selectedImageAlt, setSelectedImageAlt] = useState('');

  const [adType, setAdType] = useState('banners');

  // Separate loading states for different fetch operations
  const [loadingBanners, setLoadingBanners] = useState(false);
  const [loadingCoupons, setLoadingCoupons] = useState(false);

  // Row selection state for bulk operations
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Page range states for flexible fetching
  const [bannerPageRange, setBannerPageRange] = useState({ start: '1', end: '5' });
  const [couponPageRange, setCouponPageRange] = useState({ start: '1', end: '5' });

  // Column filter states
  const [selectedTypeFilter, setSelectedTypeFilter] = useState('');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [selectedAdvertiserFilter, setSelectedAdvertiserFilter] = useState('');
  const [showAdvertiserDropdown, setShowAdvertiserDropdown] = useState(false);
  const [selectedStartDateFilter, setSelectedStartDateFilter] = useState('');
  const [showStartDateDropdown, setShowStartDateDropdown] = useState(false);
  const [selectedEndDateFilter, setSelectedEndDateFilter] = useState('');
  const [showEndDateDropdown, setShowEndDateDropdown] = useState(false);
  const [selectedLanguageFilter, setSelectedLanguageFilter] = useState('');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  // Coupon-specific filters
  const [selectedDealStateFilter, setSelectedDealStateFilter] = useState('');
  const [showDealStateDropdown, setShowDealStateDropdown] = useState(false);
  const [selectedDealTypeFilter, setSelectedDealTypeFilter] = useState('');
  const [showDealTypeDropdown, setShowDealTypeDropdown] = useState(false);
  const [selectedDealScopeFilter, setSelectedDealScopeFilter] = useState('');
  const [showDealScopeDropdown, setShowDealScopeDropdown] = useState(false);
  const [selectedDiscountTypeFilter, setSelectedDiscountTypeFilter] = useState('');
  const [showDiscountTypeDropdown, setShowDiscountTypeDropdown] = useState(false);
  const [selectedDiscountCurrencyFilter, setSelectedDiscountCurrencyFilter] = useState('');
  const [showDiscountCurrencyDropdown, setShowDiscountCurrencyDropdown] = useState(false);



  // Process and clean ads data
  const processAdsData = (rawAds) => {
    return rawAds
      .map(ad => {
        // Clean Name field - remove image dimensions with comprehensive patterns
        let cleanName = ad.Name || '';

        // Multiple comprehensive patterns to catch all variations
        // Pattern 1: Basic dimensions with various separators: 300x250, 300X250, 300×250, 300*250, 300^250
        cleanName = cleanName.replace(/\b\d{2,4}[xX×*^]\d{2,4}\b/g, '').trim();

        // Pattern 2: Dimensions with leading separators: _300x250, -300x250, (300x250
        cleanName = cleanName.replace(/[-_(]\d{2,4}[xX×*^]\d{2,4}/g, '').trim();

        // Pattern 3: Dimensions with trailing separators: 300x250_, 300x250-, 300x250)
        cleanName = cleanName.replace(/\d{2,4}[xX×*^]\d{2,4}[-_)]/g, '').trim();

        // Pattern 4: Dimensions with spaces around: 300 x 250, 300 X 250
        cleanName = cleanName.replace(/\b\d{2,4}\s+[xX×*^]\s+\d{2,4}\b/g, '').trim();

        // Pattern 5: Attached to text without spaces: Wondershare300x250, EdrawProj300x250
        cleanName = cleanName.replace(/([a-zA-Z]+)\d{2,4}[xX×*^]\d{2,4}/g, '$1').trim();
        cleanName = cleanName.replace(/\d{2,4}[xX×*^]\d{2,4}([a-zA-Z]+)/g, '$1').trim();

        // Pattern 6: Complex combinations with multiple separators
        cleanName = cleanName.replace(/\d{2,4}[xX×*^]\d{2,4}[-_]\d{2,4}[xX×*^]\d{2,4}/g, '').trim();

        // Pattern 7: Underscore separators (specific to your examples): 1200_600, 468_60
        cleanName = cleanName.replace(/\b\d{2,4}_\d{2,4}\b/g, '').trim();

        // Pattern 8: Dash separators (specific to your examples): 600-600, 1200-628
        cleanName = cleanName.replace(/\b\d{2,4}-\d{2,4}\b/g, '').trim();

        // Pattern 9: Mixed separators with text: banner(Long-term)468_60, 1200_600
        cleanName = cleanName.replace(/\b\d{2,4}[_-]\d{2,4}\b/g, '').trim();

        // Clean up extra whitespace and normalize
        cleanName = cleanName.replace(/\s+/g, ' ').trim();
        cleanName = cleanName.replace(/^\W+|\W+$/g, '').trim(); // Remove leading/trailing punctuation

        // Clean Description field - remove image dimensions with comprehensive patterns
        let cleanDescription = ad.Description || '';

        // Multiple comprehensive patterns to catch all variations
        // Pattern 1: Basic dimensions with various separators: 300x250, 300X250, 300×250, 300*250, 300^250
        cleanDescription = cleanDescription.replace(/\b\d{2,4}[xX×*^]\d{2,4}\b/g, '').trim();

        // Pattern 2: Dimensions with leading separators: _300x250, -300x250, (300x250
        cleanDescription = cleanDescription.replace(/[-_(]\d{2,4}[xX×*^]\d{2,4}/g, '').trim();

        // Pattern 3: Dimensions with trailing separators: 300x250_, 300x250-, 300x250)
        cleanDescription = cleanDescription.replace(/\d{2,4}[xX×*^]\d{2,4}[-_)]/g, '').trim();

        // Pattern 4: Dimensions with spaces around: 300 x 250, 300 X 250
        cleanDescription = cleanDescription.replace(/\b\d{2,4}\s+[xX×*^]\s+\d{2,4}\b/g, '').trim();

        // Pattern 5: Attached to text without spaces: Wondershare300x250, EdrawProj300x250
        cleanDescription = cleanDescription.replace(/([a-zA-Z]+)\d{2,4}[xX×*^]\d{2,4}/g, '$1').trim();
        cleanDescription = cleanDescription.replace(/\d{2,4}[xX×*^]\d{2,4}([a-zA-Z]+)/g, '$1').trim();

        // Pattern 6: Complex combinations with multiple separators
        cleanDescription = cleanDescription.replace(/\d{2,4}[xX×*^]\d{2,4}[-_]\d{2,4}[xX×*^]\d{2,4}/g, '').trim();

        // Clean up extra whitespace and normalize
        cleanDescription = cleanDescription.replace(/\s+/g, ' ').trim();
        cleanDescription = cleanDescription.replace(/^\W+|\W+$/g, '').trim(); // Remove leading/trailing punctuation

        // Fill empty description with name if needed
        if (!cleanDescription && cleanName) {
          cleanDescription = cleanName;
        }

        // Process Image Dimensions - add ratios
        let processedImageDimensions = [];
        if (ad.ImageDimensions && Array.isArray(ad.ImageDimensions)) {
          processedImageDimensions = ad.ImageDimensions.map(dim => {
            // Calculate ratio if dimension matches pattern "width x height"
            const ratioMatch = dim.match(/^(\d+)\s*x\s*(\d+)$/);
            if (ratioMatch) {
              const width = parseInt(ratioMatch[1]);
              const height = parseInt(ratioMatch[2]);
              if (height > 0) {
                const ratio = (width / height).toFixed(2);
                return `${dim} :: ${ratio}`;
              }
            }
            return dim; // Return original if no ratio calculation possible
          });
        }

        return {
          ...ad,
          Name: cleanName,
          Description: cleanDescription,
          ImageDimensions: processedImageDimensions
        };
      })
      // Note: Image filtering is now handled in the backend
      // All records from backend already have small images filtered out
  };



  const fetchBanners = async () => {
    setLoadingBanners(true);
    setAdType('banners');
    setError('');
    try {
      // Construct URL with page range parameters
      const url = `/api/ads/fetch/banners/?start_page=${bannerPageRange.start}&end_page=${bannerPageRange.end}`;

      // Fetch banner ads directly - returns data immediately
      const response = await axios.post(url);

      if (response.data.success) {
        const processedData = processAdsData(response.data.data);
        setAds(processedData);
        setFilteredAds(processedData);
        setTotalPages(Math.ceil(processedData.length / pageSize));
        setCurrentPage(1);
      } else {
        setError(response.data.error);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch banner ads data');
    }
    setLoadingBanners(false);
  };

  const fetchCoupons = async () => {
    setLoadingCoupons(true);
    setAdType('coupons');
    setError('');
    try {
      // Construct URL with page range parameters
      const url = `/api/ads/fetch/coupons/?start_page=${couponPageRange.start}&end_page=${couponPageRange.end}`;

      // Fetch coupon ads directly - returns data immediately
      const response = await axios.post(url);

      if (response.data.success) {
        const processedData = processAdsData(response.data.data);
        setAds(processedData);
        setFilteredAds(processedData);
        setTotalPages(Math.ceil(processedData.length / pageSize));
        setCurrentPage(1);
      } else {
        setError(response.data.error);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch coupon ads data');
    }
    setLoadingCoupons(false);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    setTotalPages(Math.ceil(filteredAds.length / newSize));
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const removeRow = (index) => {
    const globalIndex = (currentPage - 1) * pageSize + index;
    const updatedAds = [...filteredAds];
    updatedAds.splice(globalIndex, 1);
    setFilteredAds(updatedAds);
    setTotalPages(Math.ceil(updatedAds.length / pageSize));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  };

  const downloadCSV = async () => {
    try {
      const response = await axios.post('/api/ads/download/', { ads: filteredAds }, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'impact_ads_filtered.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download CSV');
    }
  };

  // Image preview modal handlers
  const handleImageClick = (imageUrl, imageAlt) => {
    setSelectedImage(imageUrl);
    setSelectedImageAlt(imageAlt);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedImage('');
    setSelectedImageAlt('');
  };

  const handleBannerUpload = async () => {
    setError('');

    try {
      // Determine which ads to process for banner transformation
      const adsToProcess = selectedRows.size > 0
        ? Array.from(selectedRows).map(index => filteredAds[index]).filter(ad => ad)
        : filteredAds;

      console.log('Sending banner transformation request with', adsToProcess.length, 'ads');
      if (selectedRows.size > 0) {
        console.log('Processing selected ads only:', selectedRows.size, 'selected out of', filteredAds.length, 'total');
      } else {
        console.log('Processing all filtered ads:', filteredAds.length, 'ads');
      }

      // Transform selected/filtered ads to banner format
      const response = await axios.post('/api/banners/transform/', { ads: adsToProcess }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 30000, // 30 second timeout
      });

      console.log('Banner transformation response:', response.data);

      if (response.data.success) {
        // Store banner data and essential main table state in sessionStorage
        try {
          sessionStorage.setItem('bannerData', JSON.stringify(response.data.data));

          // Store only essential state, not the large ads arrays
          sessionStorage.setItem('mainTableState', JSON.stringify({
            currentPage: currentPage,
            totalPages: totalPages,
            pageSize: pageSize,
            selectedRows: Array.from(selectedRows),
            selectAll: selectAll,
            selectedTypeFilter: selectedTypeFilter,
            selectedAdvertiserFilter: selectedAdvertiserFilter,
            selectedStartDateFilter: selectedStartDateFilter,
            selectedEndDateFilter: selectedEndDateFilter,
            hasData: true, // Flag to indicate data exists
            dataLength: filteredAds.length // Store count instead of full data
          }));
          console.log('Data stored in sessionStorage successfully');
        } catch (storageErr) {
          console.error('Error storing data in sessionStorage:', storageErr);
          if (storageErr.name === 'QuotaExceededError') {
            // If quota exceeded, try storing only essential data
            try {
              sessionStorage.setItem('bannerData', JSON.stringify(response.data.data));
              sessionStorage.setItem('mainTableState', JSON.stringify({
                hasData: true,
                dataLength: filteredAds.length
              }));
              console.log('Stored minimal data due to quota limits');
            } catch (retryErr) {
              console.error('Even minimal storage failed:', retryErr);
              setError('Storage quota exceeded. Please clear browser data or try again.');
              return;
            }
          } else {
            setError('Error storing data. Please try again.');
            return;
          }
        }

        // Redirect to banner upload page
        navigate('/banners');
      } else {
        console.error('Backend returned error:', response.data.error);
        setError(response.data.error || 'Failed to transform banner data');
      }
    } catch (err) {
      console.error('Error during banner transformation:', err);
      if (err.response) {
        console.error('Response status:', err.response.status);
        console.error('Response data:', err.response.data);
        setError(err.response.data?.error || `Server error: ${err.response.status}`);
      } else if (err.request) {
        console.error('No response received:', err.request);
        setError('No response from server. Please check if the backend is running.');
      } else {
        console.error('Request setup error:', err.message);
        setError('Error setting up request: ' + err.message);
      }
    }
  };

  const handleCouponUpload = () => {
    const adsToProcess = selectedRows.size > 0
      ? Array.from(selectedRows).map(index => filteredAds[index]).filter(ad => ad)
      : filteredAds;

    sessionStorage.setItem('couponData', JSON.stringify(adsToProcess));
    navigate('/coupons');
  };

  // Type column filter handlers
  const getUniqueTypes = () => {
    const types = new Set();
    filteredAds.forEach(ad => {
      if (ad.Type) {
        types.add(ad.Type);
      }
    });
    return Array.from(types).sort();
  };

  const handleTypeFilter = (typeValue) => {
    setSelectedTypeFilter(typeValue);
    setCurrentPage(1); // Reset to first page
    setTimeout(() => {
      applyAllFilters();
      setSelectedRows(new Set()); // Clear selections when filter changes
      setSelectAll(false);
      setShowTypeDropdown(false); // Close dropdown after filter is applied
    }, 10);
  };

  // Advertiser column filter handlers
  const getUniqueAdvertisers = () => {
    const advertisers = new Set();
    filteredAds.forEach(ad => {
      if (ad.AdvertiserName) {
        advertisers.add(ad.AdvertiserName);
      }
    });
    return Array.from(advertisers).sort();
  };

  const handleAdvertiserFilter = (advertiserValue) => {
    setSelectedAdvertiserFilter(advertiserValue);
    setCurrentPage(1); // Reset to first page
    setTimeout(() => {
      applyAllFilters();
      setSelectedRows(new Set()); // Clear selections when filter changes
      setSelectAll(false);
      setShowAdvertiserDropdown(false); // Close dropdown after filter is applied
    }, 10);
  };

  // Start Date column filter handlers
  const getUniqueStartDates = () => {
    const startDates = new Set();
    filteredAds.forEach(ad => {
      if (ad.StartDate) {
        startDates.add(ad.StartDate);
      }
    });
    return Array.from(startDates).sort();
  };

  const handleStartDateFilter = (startDateValue) => {
    setSelectedStartDateFilter(startDateValue);
    setCurrentPage(1); // Reset to first page
    setTimeout(() => {
      applyAllFilters();
      setSelectedRows(new Set()); // Clear selections when filter changes
      setSelectAll(false);
      setShowStartDateDropdown(false); // Close dropdown after filter is applied
    }, 10);
  };

  // End Date column filter handlers
  const getUniqueEndDates = () => {
    const endDates = new Set();
    filteredAds.forEach(ad => {
      if (ad.EndDate) {
        endDates.add(ad.EndDate);
      }
    });
    return Array.from(endDates).sort();
  };

  const handleEndDateFilter = (endDateValue) => {
    setSelectedEndDateFilter(endDateValue);
    setCurrentPage(1); // Reset to first page
    setTimeout(() => {
      applyAllFilters();
      setSelectedRows(new Set()); // Clear selections when filter changes
      setSelectAll(false);
      setShowEndDateDropdown(false); // Close dropdown after filter is applied
    }, 10);
  };

  // Language column filter handlers
  const getUniqueLanguages = () => {
    const languages = new Set();
    filteredAds.forEach(ad => {
      if (ad.Language) {
        languages.add(ad.Language);
      }
    });
    return Array.from(languages).sort();
  };

  const handleLanguageFilter = (languageValue) => {
    setSelectedLanguageFilter(languageValue);
    setCurrentPage(1); // Reset to first page
    setTimeout(() => {
      applyAllFilters();
      setSelectedRows(new Set()); // Clear selections when filter changes
      setSelectAll(false);
      setShowLanguageDropdown(false); // Close dropdown after filter is applied
    }, 10);
  };

  // DealState column filter handlers
  const getUniqueDealStates = () => {
    const dealStates = new Set();
    filteredAds.forEach(ad => {
      if (ad.DealState) {
        dealStates.add(ad.DealState);
      }
    });
    return Array.from(dealStates).sort();
  };

  const handleDealStateFilter = (dealStateValue) => {
    setSelectedDealStateFilter(dealStateValue);
    setCurrentPage(1); // Reset to first page
    setTimeout(() => {
      applyAllFilters();
      setSelectedRows(new Set()); // Clear selections when filter changes
      setSelectAll(false);
      setShowDealStateDropdown(false); // Close dropdown after filter is applied
    }, 10);
  };

  // DealType column filter handlers
  const getUniqueDealTypes = () => {
    const dealTypes = new Set();
    filteredAds.forEach(ad => {
      if (ad.DealType) {
        dealTypes.add(ad.DealType);
      }
    });
    return Array.from(dealTypes).sort();
  };

  const handleDealTypeFilter = (dealTypeValue) => {
    setSelectedDealTypeFilter(dealTypeValue);
    setCurrentPage(1); // Reset to first page
    setTimeout(() => {
      applyAllFilters();
      setSelectedRows(new Set()); // Clear selections when filter changes
      setSelectAll(false);
      setShowDealTypeDropdown(false); // Close dropdown after filter is applied
    }, 10);
  };

  // DealScope column filter handlers
  const getUniqueDealScopes = () => {
    const dealScopes = new Set();
    filteredAds.forEach(ad => {
      if (ad.DealScope) {
        dealScopes.add(ad.DealScope);
      }
    });
    return Array.from(dealScopes).sort();
  };

  const handleDealScopeFilter = (dealScopeValue) => {
    setSelectedDealScopeFilter(dealScopeValue);
    setCurrentPage(1); // Reset to first page
    setTimeout(() => {
      applyAllFilters();
      setSelectedRows(new Set()); // Clear selections when filter changes
      setSelectAll(false);
      setShowDealScopeDropdown(false); // Close dropdown after filter is applied
    }, 10);
  };

  // DiscountType column filter handlers
  const getUniqueDiscountTypes = () => {
    const discountTypes = new Set();
    filteredAds.forEach(ad => {
      if (ad.DiscountType) {
        discountTypes.add(ad.DiscountType);
      }
    });
    return Array.from(discountTypes).sort();
  };

  const handleDiscountTypeFilter = (discountTypeValue) => {
    setSelectedDiscountTypeFilter(discountTypeValue);
    setCurrentPage(1); // Reset to first page
    setTimeout(() => {
      applyAllFilters();
      setSelectedRows(new Set()); // Clear selections when filter changes
      setSelectAll(false);
      setShowDiscountTypeDropdown(false); // Close dropdown after filter is applied
    }, 10);
  };

  // DiscountCurrency column filter handlers
  const getUniqueDiscountCurrencies = () => {
    const discountCurrencies = new Set();
    filteredAds.forEach(ad => {
      if (ad.DiscountCurrency) {
        discountCurrencies.add(ad.DiscountCurrency);
      }
    });
    return Array.from(discountCurrencies).sort();
  };

  const handleDiscountCurrencyFilter = (discountCurrencyValue) => {
    setSelectedDiscountCurrencyFilter(discountCurrencyValue);
    setCurrentPage(1); // Reset to first page
    setTimeout(() => {
      applyAllFilters();
      setSelectedRows(new Set()); // Clear selections when filter changes
      setSelectAll(false);
      setShowDiscountCurrencyDropdown(false); // Close dropdown after filter is applied
    }, 10);
  };

  // Apply all filters together (AND logic)
  const applyAllFilters = () => {
    let filtered = ads;

    // Apply Type filter
    if (selectedTypeFilter) {
      filtered = filtered.filter(ad => ad.Type === selectedTypeFilter);
    }

    // Apply Advertiser filter
    if (selectedAdvertiserFilter) {
      filtered = filtered.filter(ad => ad.AdvertiserName === selectedAdvertiserFilter);
    }

    // Apply Start Date filter
    if (selectedStartDateFilter) {
      filtered = filtered.filter(ad => ad.StartDate === selectedStartDateFilter);
    }

    // Apply End Date filter
    if (selectedEndDateFilter) {
      filtered = filtered.filter(ad => ad.EndDate === selectedEndDateFilter);
    }

    // Apply Language filter
    if (selectedLanguageFilter) {
      filtered = filtered.filter(ad => ad.Language === selectedLanguageFilter);
    }

    // Apply DealState filter
    if (selectedDealStateFilter) {
      filtered = filtered.filter(ad => ad.DealState === selectedDealStateFilter);
    }

    // Apply DealType filter
    if (selectedDealTypeFilter) {
      filtered = filtered.filter(ad => ad.DealType === selectedDealTypeFilter);
    }

    // Apply DealScope filter
    if (selectedDealScopeFilter) {
      filtered = filtered.filter(ad => ad.DealScope === selectedDealScopeFilter);
    }

    // Apply DiscountType filter
    if (selectedDiscountTypeFilter) {
      filtered = filtered.filter(ad => ad.DiscountType === selectedDiscountTypeFilter);
    }

    // Apply DiscountCurrency filter
    if (selectedDiscountCurrencyFilter) {
      filtered = filtered.filter(ad => ad.DiscountCurrency === selectedDiscountCurrencyFilter);
    }

    setFilteredAds(filtered);
    setTotalPages(Math.ceil(filtered.length / pageSize));
  };


  // Row selection handlers for bulk operations
  const handleRowSelect = (index) => {
    const globalIndex = (currentPage - 1) * pageSize + index;
    const newSelectedRows = new Set(selectedRows);

    if (newSelectedRows.has(globalIndex)) {
      newSelectedRows.delete(globalIndex);
    } else {
      newSelectedRows.add(globalIndex);
    }

    setSelectedRows(newSelectedRows);
    setSelectAll(newSelectedRows.size === getCurrentPageData().length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows(new Set());
      setSelectAll(false);
    } else {
      const allIndices = new Set();
      getCurrentPageData().forEach((_, index) => {
        allIndices.add((currentPage - 1) * pageSize + index);
      });
      setSelectedRows(allIndices);
      setSelectAll(true);
    }
  };

  const bulkRemove = () => {
    const indicesToRemove = Array.from(selectedRows).sort((a, b) => b - a);
    const updatedAds = [...filteredAds];

    indicesToRemove.forEach(index => {
      updatedAds.splice(index, 1);
    });

    setFilteredAds(updatedAds);
    setSelectedRows(new Set());
    setSelectAll(false);
    setTotalPages(Math.ceil(updatedAds.length / pageSize));

    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  };

  const clearAllFilters = () => {
    setSelectedTypeFilter('');
    setSelectedAdvertiserFilter('');
    setSelectedStartDateFilter('');
    setSelectedEndDateFilter('');
    setSelectedLanguageFilter('');
    setSelectedDealStateFilter('');
    setSelectedDealTypeFilter('');
    setSelectedDealScopeFilter('');
    setSelectedDiscountTypeFilter('');
    setSelectedDiscountCurrencyFilter('');
    setFilteredAds(ads);
    setCurrentPage(1);
    setSelectedRows(new Set());
    setSelectAll(false);
    setTotalPages(Math.ceil(ads.length / pageSize));
  };

  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAds.slice(startIndex, endIndex);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    let items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <button
          key={number}
          className={`mx-1 px-3 py-1 rounded ${
            number === currentPage
              ? 'bg-perzsi-yellow text-perzsi-gray font-semibold'
              : 'bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50'
          }`}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </button>
      );
    }

    return (
      <div className="flex justify-center items-center mt-6">
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
        >
          First
        </button>
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {items}
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          Last
        </button>
      </div>
    );
  };



  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
      {/* Header with Perzsi Branding */}
      <header className="perzsi-gradient shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div
                className="cursor-pointer"
                onClick={() => navigate('/')}
              >
                <img
                  src="/perzsi-icon-black.png"
                  alt="Perzsi.com"
                  className="h-12 w-auto"
                />
                <p className="text-yellow-100">Impact Ads Work-Area Dashboard</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-white text-right">
                <p className="text-sm opacity-90">Professional Ads Management</p>
                {getCurrentUser() && (
                  <p className="text-xs opacity-75">
                    Logged in as: {getCurrentUser().email}
                  </p>
                )}
              </div>
              <button
                onClick={toggleTheme}
                className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-lg transition-colors duration-200"
                title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
              >
                {isDark ? '☀️' : '🌙'}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="w-full px-4 py-8">
        {/* Control Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Fetch Banner Ads</h3>
            <p className={`mb-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Fetch only banner type ads from the Impact API for faster processing.
            </p>
            <div className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <div className="flex items-center space-x-3 mb-2">
                <div>
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Start Page:
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={bannerPageRange.start}
                    onChange={(e) => setBannerPageRange({...bannerPageRange, start: e.target.value})}
                    className="perzsi-input text-center w-16"
                    placeholder="1"
                  />
                </div>
                <span className="text-gray-400">to</span>
                <div>
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    End Page:
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={bannerPageRange.end}
                    onChange={(e) => setBannerPageRange({...bannerPageRange, end: e.target.value})}
                    className="perzsi-input text-center w-16"
                    placeholder="5"
                  />
                </div>
              </div>
            </div>
            <button
              className="perzsi-button-primary w-full"
              onClick={fetchBanners}
              disabled={loadingBanners || loadingCoupons}
            >
              {loadingBanners ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-perzsi-gray" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Fetching Banners...
                </span>
              ) : (
                `Fetch Banner Ads (${bannerPageRange.start}-${bannerPageRange.end})`
              )}
            </button>
          </div>

          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Fetch Coupon Ads</h3>
            <p className={`mb-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Fetch only coupon type ads from the Impact API for targeted processing.
            </p>
            <div className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <div className="flex items-center space-x-3 mb-2">
                <div>
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Start Page:
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={couponPageRange.start}
                    onChange={(e) => setCouponPageRange({...couponPageRange, start: e.target.value})}
                    className="perzsi-input text-center w-16"
                    placeholder="1"
                  />
                </div>
                <span className="text-gray-400">to</span>
                <div>
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    End Page:
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={couponPageRange.end}
                    onChange={(e) => setCouponPageRange({...couponPageRange, end: e.target.value})}
                    className="perzsi-input text-center w-16"
                    placeholder="5"
                  />
                </div>
              </div>
            </div>
            <button
              className="perzsi-button-secondary w-full"
              onClick={fetchCoupons}
              disabled={loadingBanners || loadingCoupons}
            >
              {loadingCoupons ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Fetching Coupons...
                </span>
              ) : (
                `Fetch Coupon Ads (${couponPageRange.start}-${couponPageRange.end})`
              )}
            </button>
          </div>

          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Pagination Settings</h3>
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Rows per page:
              </label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                className="perzsi-input"
              >
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={300}>300</option>
                <option value={400}>400</option>
                <option value={500}>500</option>
              </select>
            </div>
            {filteredAds.length > 0 && (
              <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <p><strong>Total Ads: {filteredAds.length}</strong></p>
                <p><strong>Showing: {getCurrentPageData().length} of {filteredAds.length}</strong></p>
              </div>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        {filteredAds.length > 0 && (
          <div className="flex flex-wrap gap-4 mb-6 justify-between">
            <div className="flex flex-wrap gap-4">
              <button
                className="perzsi-button-primary"
                onClick={downloadCSV}
              >
                Download Filtered Ads as CSV
              </button>
              <button
                className="perzsi-button-secondary"
                onClick={handleBannerUpload}
                disabled={filteredAds.length === 0}
              >
                Banner Upload Table
              </button>
              <button
                className="perzsi-button-secondary"
                onClick={handleCouponUpload}
                disabled={filteredAds.length === 0}
              >
                Coupon Upload Table
              </button>
              {selectedRows.size > 0 && (
                <button
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
                  onClick={bulkRemove}
                >
                  Remove Bulk Items ({selectedRows.size})
                </button>
              )}
            </div>
            <div className="flex items-center">
              {(selectedTypeFilter || selectedAdvertiserFilter || selectedStartDateFilter || selectedEndDateFilter || selectedLanguageFilter || selectedDealStateFilter || selectedDealTypeFilter || selectedDealScopeFilter || selectedDiscountTypeFilter || selectedDiscountCurrencyFilter) && (
                <button
                  className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
                  onClick={clearAllFilters}
                >
                  Clear All Filters
                </button>
              )}
            </div>
          </div>
        )}

        {/* Top Pagination */}
        {filteredAds.length > 0 && totalPages > 1 && (
          <div className="flex justify-center items-center mb-4">
            <button
              className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            >
              First
            </button>
            <button
              className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {(() => {
              let items = [];
              const maxVisiblePages = 5;
              let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
              let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

              if (endPage - startPage + 1 < maxVisiblePages) {
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
              }

              for (let number = startPage; number <= endPage; number++) {
                items.push(
                  <button
                    key={number}
                    className={`mx-1 px-3 py-1 rounded ${
                      number === currentPage
                        ? 'bg-perzsi-yellow text-perzsi-gray font-semibold'
                        : 'bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => handlePageChange(number)}
                  >
                    {number}
                  </button>
                );
              }
              return items;
            })()}
            <button
              className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
            <button
              className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </button>
          </div>
        )}

        {/* Ads Table */}
        {filteredAds.length > 0 && (
          adType === 'banners' ? (
            <BannerTable
              ads={getCurrentPageData()}
              selectedRows={selectedRows}
              handleRowSelect={handleRowSelect}
              handleSelectAll={handleSelectAll}
              selectAll={selectAll}
              removeRow={removeRow}
              handleImageClick={handleImageClick}
              isDark={isDark}
              currentPage={currentPage}
              pageSize={pageSize}
              // Advertiser Filter
              selectedAdvertiserFilter={selectedAdvertiserFilter}
              showAdvertiserDropdown={showAdvertiserDropdown}
              setShowAdvertiserDropdown={setShowAdvertiserDropdown}
              getUniqueAdvertisers={getUniqueAdvertisers}
              handleAdvertiserFilter={handleAdvertiserFilter}
              // Type Filter
              selectedTypeFilter={selectedTypeFilter}
              showTypeDropdown={showTypeDropdown}
              setShowTypeDropdown={setShowTypeDropdown}
              getUniqueTypes={getUniqueTypes}
              handleTypeFilter={handleTypeFilter}
              // Language Filter
              selectedLanguageFilter={selectedLanguageFilter}
              showLanguageDropdown={showLanguageDropdown}
              setShowLanguageDropdown={setShowLanguageDropdown}
              getUniqueLanguages={getUniqueLanguages}
              handleLanguageFilter={handleLanguageFilter}
              // Start Date Filter
              selectedStartDateFilter={selectedStartDateFilter}
              showStartDateDropdown={showStartDateDropdown}
              setShowStartDateDropdown={setShowStartDateDropdown}
              getUniqueStartDates={getUniqueStartDates}
              handleStartDateFilter={handleStartDateFilter}
              // End Date Filter
              selectedEndDateFilter={selectedEndDateFilter}
              showEndDateDropdown={showEndDateDropdown}
              setShowEndDateDropdown={setShowEndDateDropdown}
              getUniqueEndDates={getUniqueEndDates}
              handleEndDateFilter={handleEndDateFilter}
            />
          ) : (
            <CouponTable
              ads={getCurrentPageData()}
              selectedRows={selectedRows}
              handleRowSelect={handleRowSelect}
              handleSelectAll={handleSelectAll}
              selectAll={selectAll}
              removeRow={removeRow}
              isDark={isDark}
              currentPage={currentPage}
              pageSize={pageSize}
              // Deal State Filter
              selectedDealStateFilter={selectedDealStateFilter}
              showDealStateDropdown={showDealStateDropdown}
              setShowDealStateDropdown={setShowDealStateDropdown}
              getUniqueDealStates={getUniqueDealStates}
              handleDealStateFilter={handleDealStateFilter}
              // Deal Type Filter
              selectedDealTypeFilter={selectedDealTypeFilter}
              showDealTypeDropdown={showDealTypeDropdown}
              setShowDealTypeDropdown={setShowDealTypeDropdown}
              getUniqueDealTypes={getUniqueDealTypes}
              handleDealTypeFilter={handleDealTypeFilter}
              // Deal Scope Filter
              selectedDealScopeFilter={selectedDealScopeFilter}
              showDealScopeDropdown={showDealScopeDropdown}
              setShowDealScopeDropdown={setShowDealScopeDropdown}
              getUniqueDealScopes={getUniqueDealScopes}
              handleDealScopeFilter={handleDealScopeFilter}
              // Discount Type Filter
              selectedDiscountTypeFilter={selectedDiscountTypeFilter}
              showDiscountTypeDropdown={showDiscountTypeDropdown}
              setShowDiscountTypeDropdown={setShowDiscountTypeDropdown}
              getUniqueDiscountTypes={getUniqueDiscountTypes}
              handleDiscountTypeFilter={handleDiscountTypeFilter}
              // Discount Currency Filter
              selectedDiscountCurrencyFilter={selectedDiscountCurrencyFilter}
              showDiscountCurrencyDropdown={showDiscountCurrencyDropdown}
              setShowDiscountCurrencyDropdown={setShowDiscountCurrencyDropdown}
              getUniqueDiscountCurrencies={getUniqueDiscountCurrencies}
              handleDiscountCurrencyFilter={handleDiscountCurrencyFilter}
              // Advertiser Filter
              selectedAdvertiserFilter={selectedAdvertiserFilter}
              showAdvertiserDropdown={showAdvertiserDropdown}
              setShowAdvertiserDropdown={setShowAdvertiserDropdown}
              getUniqueAdvertisers={getUniqueAdvertisers}
              handleAdvertiserFilter={handleAdvertiserFilter}
              // Type Filter
              selectedTypeFilter={selectedTypeFilter}
              showTypeDropdown={showTypeDropdown}
              setShowTypeDropdown={setShowTypeDropdown}
              getUniqueTypes={getUniqueTypes}
              handleTypeFilter={handleTypeFilter}
            />
          )
        )}

        {/* Pagination */}
        {renderPagination()}

        {/* Back Button */}
        <div className="mt-6 text-center">
          <button
            className="perzsi-button-secondary"
            onClick={() => navigate('/')}
          >
            ← Back to Home
          </button>
        </div>

        {/* Image Preview Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold text-perzsi-gray">Image Preview</h3>
                  <button
                    onClick={handleCloseModal}
                    className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                  >
                    ×
                  </button>
                </div>
                <div className="text-center">
                  {selectedImage && (
                    <img
                      src={selectedImage}
                      alt={selectedImageAlt}
                      className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    className="perzsi-button-primary"
                    onClick={handleCloseModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
